<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Notifications</h1>
        <div class="gl-breadcrumb">
          <router-link to="/">Home</router-link>
          <span class="gl-breadcrumb-sep">/</span>
          <span>Notifications</span>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="gl-tab-bar">
      <button class="gl-tab" :class="{ 'gl-tab-active': tab === 'compose' }" @click="tab = 'compose'">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        Compose
      </button>
      <button class="gl-tab" :class="{ 'gl-tab-active': tab === 'history' }" @click="tab = 'history'; fetchHistory()">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        History
      </button>
    </div>

    <!-- Compose Tab -->
    <div v-if="tab === 'compose'" class="gl-compose-layout">
      <!-- Form Panel -->
      <div class="gl-form-panel">
        <form @submit.prevent="openSendConfirm">

          <!-- Audience -->
          <div class="gl-field-group">
            <label class="gl-label">Audience <span class="gl-required">*</span></label>
            <div class="gl-audience-row">
              <button
                type="button"
                class="gl-audience-card"
                :class="{ 'gl-audience-active': form.audience_type === 'customers' }"
                @click="form.audience_type = 'customers'; recipientCount = null"
              >
                <div class="gl-aud-icon gl-aud-teal">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </div>
                <div>
                  <div class="gl-aud-label">Customers</div>
                  <div class="gl-aud-sub">App users</div>
                </div>
                <div class="gl-aud-check">
                  <svg v-if="form.audience_type === 'customers'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
              </button>
              <button
                type="button"
                class="gl-audience-card"
                :class="{ 'gl-audience-active': form.audience_type === 'riders' }"
                @click="form.audience_type = 'riders'; recipientCount = null"
              >
                <div class="gl-aud-icon gl-aud-blue">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                <div>
                  <div class="gl-aud-label">Riders</div>
                  <div class="gl-aud-sub">Delivery partners</div>
                </div>
                <div class="gl-aud-check">
                  <svg v-if="form.audience_type === 'riders'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
              </button>
            </div>
          </div>

          <!-- Filters -->
          <div class="gl-filters-row">
            <div v-if="form.audience_type === 'customers'" class="gl-field-group">
              <label class="gl-label">Filter by Tier</label>
              <div class="gl-select-wrap">
                <select class="gl-select" v-model="form.filters.tier_id" @change="recipientCount = null">
                  <option value="">All tiers</option>
                  <option v-for="t in tiers" :key="t.id" :value="t.id">{{ t.name }}</option>
                </select>
                <svg class="gl-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
            <div class="gl-field-group">
              <label class="gl-label">Filter by Branch</label>
              <div class="gl-select-wrap">
                <select class="gl-select" v-model="form.filters.outlet_id" @change="recipientCount = null">
                  <option value="">All branches</option>
                  <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
                </select>
                <svg class="gl-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
          </div>

          <!-- Title -->
          <div class="gl-field-group">
            <label class="gl-label">Title <span class="gl-required">*</span></label>
            <input
              type="text"
              class="gl-input"
              v-model="form.title"
              maxlength="100"
              placeholder="e.g. Weekend Flash Sale — 20% Off!"
              required
            >
            <div class="gl-char-count">{{ form.title.length }}/100</div>
          </div>

          <!-- Message -->
          <div class="gl-field-group">
            <label class="gl-label">Message <span class="gl-required">*</span></label>
            <textarea
              class="gl-textarea"
              v-model="form.body"
              rows="4"
              maxlength="500"
              placeholder="Write a short, clear message that compels action..."
              required
            ></textarea>
            <div class="gl-char-count">{{ form.body.length }}/500</div>
          </div>

          <!-- Deep Link -->
          <div class="gl-field-group">
            <label class="gl-label">
              Deep Link
              <span class="gl-label-optional">optional</span>
            </label>
            <div class="gl-input-prefix-wrap">
              <span class="gl-input-prefix">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              </span>
              <input type="text" class="gl-input gl-input-prefixed" v-model="form.link" placeholder="/products/123">
            </div>
            <div class="gl-hint">Relative path opened when the user taps the notification</div>
          </div>

          <!-- Actions -->
          <div class="gl-form-actions">
            <div class="gl-preview-row">
              <button type="button" class="gl-btn-preview" @click="previewRecipients" :disabled="previewing">
                <span v-if="previewing" class="gl-spinner"></span>
                <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                Preview Recipients
              </button>
              <transition name="gl-fade">
                <div v-if="recipientCount !== null" class="gl-recipient-badge">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                  <strong>{{ recipientCount }}</strong> recipient{{ recipientCount !== 1 ? 's' : '' }}
                </div>
              </transition>
            </div>

            <div class="gl-send-row">
              <button type="submit" class="gl-btn-send" :disabled="sending || !canSend">
                <span v-if="sending" class="gl-spinner gl-spinner-white"></span>
                <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                Send Now
              </button>
              <button type="button" class="gl-btn-ghost" @click="resetForm">Reset</button>
            </div>
          </div>
        </form>
      </div>

      <!-- Phone Preview -->
      <div class="gl-preview-panel">
        <div class="gl-preview-label">Live Preview</div>
        <div class="gl-phone-wrap">
          <div class="gl-phone">
            <div class="gl-phone-island"></div>
            <div class="gl-phone-screen">
              <div class="gl-phone-status">
                <span>9:41</span>
                <div class="gl-phone-status-icons">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M1.5 8.5a13 13 0 0 1 21 0M5 12a10 10 0 0 1 14 0M8.5 15.5a6 6 0 0 1 7 0M12 19h.01"/></svg>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3H8a2 2 0 0 0-2 2v2h12V5a2 2 0 0 0-2-2z" opacity=".5"/></svg>
                </div>
              </div>

              <div class="gl-phone-notif">
                <div class="gl-notif-header">
                  <div class="gl-notif-app-icon">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  </div>
                  <span class="gl-notif-app-name">GetLokal</span>
                  <span class="gl-notif-time">now</span>
                </div>
                <div class="gl-notif-title">{{ form.title || 'Your title' }}</div>
                <div class="gl-notif-body">{{ form.body || 'Your message will appear here.' }}</div>
              </div>

              <div class="gl-phone-bg-row">
                <div class="gl-phone-bg-block"></div>
                <div class="gl-phone-bg-block gl-bg-sm"></div>
              </div>
              <div class="gl-phone-bg-row">
                <div class="gl-phone-bg-block gl-bg-full"></div>
              </div>
            </div>
            <div class="gl-phone-bar"></div>
          </div>
        </div>

        <!-- Audience info chip -->
        <div class="gl-preview-meta">
          <div class="gl-preview-meta-chip">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
            {{ form.audience_type === 'customers' ? 'Customers' : 'Riders' }}
            <template v-if="form.filters.tier_id">· {{ tierName(form.filters.tier_id) }}</template>
            <template v-if="form.filters.outlet_id">· {{ branchName(form.filters.outlet_id) }}</template>
          </div>
          <div v-if="form.link" class="gl-preview-meta-chip gl-chip-link">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
            {{ form.link }}
          </div>
        </div>
      </div>
    </div>

    <!-- History Tab -->
    <div v-if="tab === 'history'" class="gl-history-wrap">
      <!-- Loading -->
      <div v-if="loadingHistory" class="gl-history-skel">
        <div v-for="i in 4" :key="i" class="gl-skel-card">
          <div class="gl-skel-row">
            <div class="gl-skel gl-skel-circle"></div>
            <div style="flex:1">
              <div class="gl-skel gl-skel-line" style="width:40%;margin-bottom:6px"></div>
              <div class="gl-skel gl-skel-line" style="width:70%"></div>
            </div>
            <div class="gl-skel gl-skel-line" style="width:60px"></div>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="!broadcasts.length" class="gl-empty">
        <div class="gl-empty-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        </div>
        <div class="gl-empty-title">No broadcasts yet</div>
        <div class="gl-empty-sub">Messages you send will appear here</div>
        <button class="gl-btn-send" style="margin-top:16px" @click="tab = 'compose'">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          Compose Now
        </button>
      </div>

      <!-- List -->
      <div v-else class="gl-history-list">
        <div v-for="b in broadcasts" :key="b.id" class="gl-history-card">
          <div class="gl-history-icon-wrap">
            <div class="gl-history-icon" :class="b.audience_type === 'riders' ? 'gl-hist-blue' : 'gl-hist-teal'">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </div>
          </div>
          <div class="gl-history-body">
            <div class="gl-history-top">
              <span class="gl-history-title">{{ b.title }}</span>
              <span class="gl-audience-badge" :class="b.audience_type === 'riders' ? 'gl-badge-blue' : 'gl-badge-teal'">
                {{ b.audience_type === 'riders' ? 'Riders' : 'Customers' }}
              </span>
            </div>
            <div class="gl-history-msg">{{ b.body }}</div>
            <div class="gl-history-meta">
              <span>{{ formatDate(b.sent_at || b.created_at) }}</span>
              <span class="gl-meta-dot"></span>
              <span>{{ b.recipient_count ?? '—' }} recipients</span>
              <template v-if="b.link">
                <span class="gl-meta-dot"></span>
                <span class="gl-meta-link">{{ b.link }}</span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Send Confirm Modal -->
    <div v-if="confirmOpen" class="gl-modal-backdrop" @click.self="confirmOpen = false">
      <div class="gl-modal gl-modal-sm">
        <div class="gl-modal-send-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </div>
        <h3 class="gl-modal-title">Send Notification</h3>
        <p class="gl-modal-sub">
          This will broadcast <strong>{{ form.title }}</strong> to
          <template v-if="recipientCount !== null"><strong>{{ recipientCount }}</strong> {{ form.audience_type }}.</template>
          <template v-else>all <strong>{{ form.audience_type }}</strong>.</template>
        </p>
        <div class="gl-modal-preview">
          <div class="gl-modal-notif">
            <div class="gl-modal-notif-header">
              <div class="gl-notif-app-icon">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
              </div>
              <span class="gl-notif-app-name">GetLokal</span>
            </div>
            <div class="gl-notif-title" style="font-size:13px">{{ form.title }}</div>
            <div class="gl-notif-body" style="font-size:12px">{{ form.body }}</div>
          </div>
        </div>
        <div class="gl-modal-actions">
          <button class="gl-btn-ghost" @click="confirmOpen = false">Cancel</button>
          <button class="gl-btn-send" :disabled="sending" @click="confirmSend">
            <span v-if="sending" class="gl-spinner gl-spinner-white"></span>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            Yes, Send
          </button>
        </div>
      </div>
    </div>

    <!-- Success Toast -->
    <transition name="gl-toast">
      <div v-if="toastMsg" class="gl-toast gl-toast-success">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        {{ toastMsg }}
      </div>
    </transition>
  </div>
</template>

<script>
import { ownerNotificationApi, ownerBranchApi, ownerCustomerApi } from '@/services/ownerApi';

export default {
  name: 'OwnerNotifications',
  data() {
    return {
      tab: 'compose',
      form: {
        audience_type: 'customers',
        filters: { tier_id: '', outlet_id: '' },
        title: '',
        body: '',
        link: ''
      },
      tiers: [],
      branches: [],
      recipientCount: null,
      previewing: false,
      sending: false,
      broadcasts: [],
      loadingHistory: false,
      confirmOpen: false,
      toastMsg: null,
      toastTimer: null,
    };
  },
  computed: {
    canSend() {
      return this.form.title.trim() && this.form.body.trim();
    }
  },
  mounted() {
    this.fetchBranches();
    this.fetchTiers();
  },
  methods: {
    async fetchBranches() {
      try {
        const r = await ownerBranchApi.list({ per_page: 100 });
        this.branches = r.data.data;
      } catch {}
    },
    async fetchTiers() {
      try {
        const r = await ownerCustomerApi.getTiers();
        this.tiers = r.data.data;
      } catch {}
    },
    buildPayload() {
      const filters = {};
      if (this.form.filters.tier_id) filters.tier_id = Number(this.form.filters.tier_id);
      if (this.form.filters.outlet_id) filters.outlet_id = Number(this.form.filters.outlet_id);
      return {
        audience_type: this.form.audience_type,
        filters,
        title: this.form.title,
        body: this.form.body,
        link: this.form.link || null
      };
    },
    async previewRecipients() {
      try {
        this.previewing = true;
        const r = await ownerNotificationApi.preview(this.buildPayload());
        this.recipientCount = r.data.data.recipient_count;
      } catch (e) {
        this.showToast(e.response?.data?.message || 'Failed to preview', 'error');
      } finally {
        this.previewing = false;
      }
    },
    openSendConfirm() {
      if (!this.canSend) return;
      this.confirmOpen = true;
    },
    async confirmSend() {
      try {
        this.sending = true;
        const r = await ownerNotificationApi.send(this.buildPayload());
        this.confirmOpen = false;
        this.showToast(r.data.message || 'Notification sent successfully!');
        this.resetForm();
        this.tab = 'history';
        this.fetchHistory();
      } catch (e) {
        this.showToast(e.response?.data?.message || 'Failed to send', 'error');
      } finally {
        this.sending = false;
      }
    },
    resetForm() {
      this.form = {
        audience_type: this.form.audience_type,
        filters: { tier_id: '', outlet_id: '' },
        title: '',
        body: '',
        link: ''
      };
      this.recipientCount = null;
    },
    async fetchHistory() {
      try {
        this.loadingHistory = true;
        const r = await ownerNotificationApi.list({ per_page: 50 });
        this.broadcasts = r.data.data;
      } catch {} finally {
        this.loadingHistory = false;
      }
    },
    formatDate(v) {
      if (!v) return '—';
      const d = new Date(v);
      return d.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })
        + ', ' + d.toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' });
    },
    tierName(id) {
      return this.tiers.find(t => t.id == id)?.name || id;
    },
    branchName(id) {
      return this.branches.find(b => b.id == id)?.name || id;
    },
    showToast(msg) {
      this.toastMsg = msg;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { this.toastMsg = null; }, 3500);
    },
  }
};
</script>

<style scoped>
/* ── Page layout ── */
.gl-page { padding: 28px 32px; max-width: 1200px; }
.gl-page-header { margin-bottom: 24px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #1a2e2b; margin: 0 0 4px; letter-spacing: -0.3px; }
.gl-breadcrumb { font-size: 12px; color: #8a9e9a; display: flex; align-items: center; gap: 6px; }
.gl-breadcrumb a { color: #8a9e9a; text-decoration: none; }
.gl-breadcrumb a:hover { color: #900C27; }
.gl-breadcrumb-sep { opacity: .5; }

/* ── Tabs ── */
.gl-tab-bar { display: flex; gap: 2px; border-bottom: 2px solid #eef0ef; margin-bottom: 28px; }
.gl-tab { display: flex; align-items: center; gap: 7px; padding: 10px 18px; font-size: 13.5px; font-weight: 500; color: #6b7f7b; background: none; border: none; border-bottom: 2px solid transparent; margin-bottom: -2px; cursor: pointer; transition: color .15s, border-color .15s; }
.gl-tab:hover { color: #900C27; }
.gl-tab-active { color: #900C27; border-bottom-color: #900C27; font-weight: 600; }

/* ── Compose Layout ── */
.gl-compose-layout { display: grid; grid-template-columns: 1fr 320px; gap: 24px; align-items: start; }
.gl-form-panel { background: #fff; border-radius: 14px; border: 1px solid #eaedec; padding: 28px; }

/* ── Fields ── */
.gl-field-group { margin-bottom: 20px; }
.gl-label { display: block; font-size: 12.5px; font-weight: 600; color: #3d5450; margin-bottom: 8px; letter-spacing: .2px; text-transform: uppercase; }
.gl-required { color: #e05252; margin-left: 1px; }
.gl-label-optional { font-size: 11px; font-weight: 400; color: #9db5b0; text-transform: none; margin-left: 6px; }

.gl-input, .gl-textarea, .gl-select {
  width: 100%; padding: 10px 13px; font-size: 13.5px; color: #1a2e2b;
  background: #f8faf9; border: 1.5px solid #e0e9e7; border-radius: 8px;
  outline: none; transition: border-color .15s, background .15s;
  font-family: inherit; box-sizing: border-box;
}
.gl-input:focus, .gl-textarea:focus { border-color: #900C27; background: #fff; box-shadow: 0 0 0 3px rgba(144,12,39,.08); }
.gl-textarea { resize: vertical; min-height: 110px; }

.gl-select-wrap { position: relative; }
.gl-select { appearance: none; padding-right: 34px; cursor: pointer; }
.gl-select:focus { border-color: #900C27; background: #fff; box-shadow: 0 0 0 3px rgba(144,12,39,.08); }
.gl-select-chevron { position: absolute; right: 11px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #6b7f7b; }

.gl-input-prefix-wrap { position: relative; }
.gl-input-prefix { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9db5b0; }
.gl-input-prefixed { padding-left: 34px; }

.gl-char-count { font-size: 11px; color: #9db5b0; text-align: right; margin-top: 4px; }
.gl-hint { font-size: 11.5px; color: #8a9e9a; margin-top: 5px; }

/* ── Filters row ── */
.gl-filters-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

/* ── Audience cards ── */
.gl-audience-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.gl-audience-card {
  display: flex; align-items: center; gap: 12px; padding: 13px 14px;
  background: #f8faf9; border: 1.5px solid #e0e9e7; border-radius: 10px;
  cursor: pointer; text-align: left; transition: border-color .15s, background .15s, box-shadow .15s;
}
.gl-audience-card:hover { border-color: #900C27; background: #f0f8f5; }
.gl-audience-active { border-color: #900C27 !important; background: #f0f8f5 !important; box-shadow: 0 0 0 3px rgba(144,12,39,.1); }

.gl-aud-icon { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-aud-teal { background: #e0f0ec; color: #900C27; }
.gl-aud-blue { background: #dbeafe; color: #2563eb; }
.gl-aud-label { font-size: 13px; font-weight: 600; color: #1a2e2b; }
.gl-aud-sub { font-size: 11px; color: #8a9e9a; }
.gl-aud-check { margin-left: auto; width: 20px; height: 20px; border-radius: 50%; background: #900C27; color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; opacity: 0; transition: opacity .15s; }
.gl-audience-active .gl-aud-check { opacity: 1; }

/* ── Form actions ── */
.gl-form-actions { border-top: 1.5px solid #eef0ef; padding-top: 20px; margin-top: 4px; }
.gl-preview-row { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.gl-send-row { display: flex; align-items: center; gap: 10px; }

.gl-btn-send {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 10px 20px; font-size: 13.5px; font-weight: 600;
  background: #900C27; color: #fff; border: none; border-radius: 8px;
  cursor: pointer; transition: background .15s, transform .1s;
}
.gl-btn-send:hover:not(:disabled) { background: #1a5248; }
.gl-btn-send:active:not(:disabled) { transform: scale(.98); }
.gl-btn-send:disabled { opacity: .55; cursor: not-allowed; }

.gl-btn-ghost {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 10px 18px; font-size: 13px; font-weight: 500;
  background: #fff; color: #4a6360; border: 1.5px solid #d4dedd;
  border-radius: 8px; cursor: pointer; transition: border-color .15s, background .15s;
}
.gl-btn-ghost:hover { border-color: #b0c4c0; background: #f8faf9; }

.gl-btn-preview {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 16px; font-size: 12.5px; font-weight: 500;
  background: #fff; color: #900C27; border: 1.5px solid #900C27;
  border-radius: 8px; cursor: pointer; transition: background .15s;
}
.gl-btn-preview:hover:not(:disabled) { background: #f0f8f5; }
.gl-btn-preview:disabled { opacity: .5; cursor: not-allowed; }

.gl-recipient-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 12px; background: #e8f5f0; color: #1a5248;
  border-radius: 20px; font-size: 12.5px; font-weight: 500;
}

/* ── Spinner ── */
.gl-spinner {
  display: inline-block; width: 13px; height: 13px;
  border: 2px solid rgba(144,12,39,.25); border-top-color: #900C27;
  border-radius: 50%; animation: gl-spin .65s linear infinite;
}
.gl-spinner-white { border-color: rgba(255,255,255,.3); border-top-color: #fff; }
@keyframes gl-spin { to { transform: rotate(360deg); } }

/* ── Preview Panel ── */
.gl-preview-panel { position: sticky; top: 90px; }
.gl-preview-label { font-size: 11px; font-weight: 700; color: #8a9e9a; text-transform: uppercase; letter-spacing: .8px; margin-bottom: 16px; }

.gl-phone-wrap { display: flex; justify-content: center; }
.gl-phone {
  width: 220px; min-height: 380px; background: #1c1c1e; border-radius: 36px;
  padding: 16px 14px 20px; box-shadow: 0 24px 60px rgba(0,0,0,.22), inset 0 0 0 1px rgba(255,255,255,.05);
  display: flex; flex-direction: column; align-items: stretch; position: relative;
}
.gl-phone-island {
  width: 80px; height: 26px; background: #000; border-radius: 20px;
  margin: 0 auto 12px; position: relative; z-index: 1;
}
.gl-phone-screen { background: #2c2c2e; border-radius: 24px; flex: 1; padding: 12px; overflow: hidden; }
.gl-phone-status { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; color: rgba(255,255,255,.9); font-size: 11px; font-weight: 600; }
.gl-phone-status-icons { display: flex; gap: 4px; align-items: center; }
.gl-phone-bar { width: 90px; height: 4px; background: rgba(255,255,255,.3); border-radius: 4px; margin: 14px auto 0; }

.gl-phone-notif {
  background: rgba(255,255,255,.12); backdrop-filter: blur(12px);
  border-radius: 14px; padding: 11px 12px;
  border: 1px solid rgba(255,255,255,.08);
  animation: gl-notif-in .3s ease;
}
@keyframes gl-notif-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
.gl-notif-header { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
.gl-notif-app-icon { width: 18px; height: 18px; border-radius: 5px; background: #900C27; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-notif-app-name { font-size: 11px; font-weight: 600; color: rgba(255,255,255,.7); flex: 1; }
.gl-notif-time { font-size: 10px; color: rgba(255,255,255,.4); }
.gl-notif-title { font-size: 12px; font-weight: 600; color: rgba(255,255,255,.95); line-height: 1.3; word-break: break-word; }
.gl-notif-body { font-size: 11px; color: rgba(255,255,255,.6); margin-top: 3px; line-height: 1.4; word-break: break-word; }

.gl-phone-bg-row { display: flex; gap: 6px; margin-top: 8px; }
.gl-phone-bg-block { height: 50px; flex: 1; background: rgba(255,255,255,.06); border-radius: 10px; }
.gl-bg-sm { flex: 0.6; }
.gl-bg-full { flex: 1; }

/* ── Preview meta chips ── */
.gl-preview-meta { margin-top: 16px; display: flex; flex-direction: column; gap: 6px; }
.gl-preview-meta-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 10px; background: #eef5f3; color: #900C27;
  border-radius: 20px; font-size: 11.5px; font-weight: 500;
}
.gl-chip-link { background: #e8f0fd; color: #2563eb; word-break: break-all; }

/* ── History ── */
.gl-history-wrap { max-width: 820px; }
.gl-history-list { display: flex; flex-direction: column; gap: 12px; }

.gl-history-card {
  display: flex; gap: 14px; align-items: flex-start;
  background: #fff; border: 1px solid #eaedec; border-radius: 12px;
  padding: 16px 18px; transition: box-shadow .15s;
}
.gl-history-card:hover { box-shadow: 0 3px 12px rgba(0,0,0,.07); }

.gl-history-icon-wrap { flex-shrink: 0; }
.gl-history-icon {
  width: 36px; height: 36px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
}
.gl-hist-teal { background: #e0f0ec; color: #900C27; }
.gl-hist-blue { background: #dbeafe; color: #2563eb; }

.gl-history-body { flex: 1; min-width: 0; }
.gl-history-top { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 4px; flex-wrap: wrap; }
.gl-history-title { font-size: 14px; font-weight: 600; color: #1a2e2b; }
.gl-audience-badge { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 20px; flex-shrink: 0; margin-top: 1px; }
.gl-badge-teal { background: #e0f0ec; color: #1a5248; }
.gl-badge-blue { background: #dbeafe; color: #1d4ed8; }
.gl-history-msg { font-size: 13px; color: #5a7470; line-height: 1.4; margin-bottom: 8px; }
.gl-history-meta { display: flex; align-items: center; gap: 7px; font-size: 12px; color: #8a9e9a; flex-wrap: wrap; }
.gl-meta-dot { width: 3px; height: 3px; border-radius: 50%; background: #c0ccc9; }
.gl-meta-link { color: #2563eb; }

/* ── Skeleton ── */
.gl-history-skel { display: flex; flex-direction: column; gap: 12px; }
.gl-skel-card { background: #fff; border: 1px solid #eaedec; border-radius: 12px; padding: 16px 18px; }
.gl-skel-row { display: flex; gap: 14px; align-items: center; }
.gl-skel { background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
.gl-skel-circle { width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0; }
.gl-skel-line { height: 12px; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

/* ── Empty ── */
.gl-empty { text-align: center; padding: 64px 24px; background: #fff; border: 1px solid #eaedec; border-radius: 14px; }
.gl-empty-icon { width: 56px; height: 56px; border-radius: 16px; background: #eef5f3; color: #900C27; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
.gl-empty-title { font-size: 16px; font-weight: 600; color: #1a2e2b; margin-bottom: 6px; }
.gl-empty-sub { font-size: 13px; color: #8a9e9a; }

/* ── Modal ── */
.gl-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 24px; }
.gl-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 420px; padding: 28px; box-shadow: 0 24px 60px rgba(0,0,0,.18); animation: gl-modal-in .2s ease; }
.gl-modal-sm { max-width: 420px; }
@keyframes gl-modal-in { from { opacity: 0; transform: scale(.96) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }

.gl-modal-send-icon { width: 48px; height: 48px; border-radius: 14px; background: linear-gradient(135deg, #900C27, #1a5248); color: #fff; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
.gl-modal-title { font-size: 17px; font-weight: 700; color: #1a2e2b; margin: 0 0 6px; }
.gl-modal-sub { font-size: 13.5px; color: #5a7470; line-height: 1.5; margin-bottom: 16px; }

.gl-modal-preview { background: #1c1c1e; border-radius: 14px; padding: 14px; margin-bottom: 20px; }
.gl-modal-notif { background: rgba(255,255,255,.12); border-radius: 12px; padding: 11px 12px; border: 1px solid rgba(255,255,255,.08); }
.gl-modal-notif-header { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }

.gl-modal-actions { display: flex; gap: 10px; justify-content: flex-end; }

/* ── Toast ── */
.gl-toast {
  position: fixed; bottom: 28px; right: 28px; z-index: 2000;
  display: flex; align-items: center; gap: 9px;
  padding: 12px 18px; border-radius: 10px; font-size: 13.5px; font-weight: 500;
  box-shadow: 0 8px 24px rgba(0,0,0,.14);
}
.gl-toast-success { background: #1a5248; color: #fff; }
.gl-toast-enter-active, .gl-toast-leave-active { transition: all .3s ease; }
.gl-toast-enter-from { opacity: 0; transform: translateY(12px) scale(.95); }
.gl-toast-leave-to { opacity: 0; transform: translateY(12px) scale(.95); }

/* ── Fade transition ── */
.gl-fade-enter-active, .gl-fade-leave-active { transition: opacity .2s; }
.gl-fade-enter-from, .gl-fade-leave-to { opacity: 0; }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-title { font-size: 18px; }
  .gl-breadcrumb { display: none; }
  .gl-page-header { margin-bottom: 16px; }
  .gl-tab-bar { margin-bottom: 16px; }

  /* Compose: stack form above preview */
  .gl-compose-layout { grid-template-columns: 1fr; gap: 20px; }
  .gl-form-panel { min-width: 0; padding: 16px; overflow-x: hidden; }
  .gl-preview-panel { position: static; }

  /* Audience cards: single column on mobile for better tap targets */
  .gl-audience-row { grid-template-columns: 1fr; gap: 8px; }
  .gl-audience-card { padding: 11px 13px; }

  /* Filters row: single column */
  .gl-filters-row { grid-template-columns: 1fr; gap: 12px; }

  /* Form actions */
  .gl-preview-row { flex-wrap: wrap; }
  .gl-send-row { flex-wrap: wrap; }
  .gl-btn-send, .gl-btn-ghost { width: 100%; justify-content: center; }

  /* History */
  .gl-history-card { padding: 12px 14px; gap: 10px; }

  /* Modal */
  .gl-modal-backdrop { padding: 68px 16px 16px; align-items: center; }
  .gl-modal { border-radius: 16px; padding: 20px; max-height: calc(100vh - 84px); overflow-y: auto; }

  /* Toast: full width on mobile */
  .gl-toast { bottom: 16px; right: 16px; left: 16px; }
}
</style>

<style>
/* ── Dark mode ── */
body.dark-only .gl-page-title { color: #e8f0ee; }
body.dark-only .gl-breadcrumb, body.dark-only .gl-breadcrumb a { color: #5a7470; }
body.dark-only .gl-tab { color: #5a7470; }
body.dark-only .gl-tab:hover { color: #4caf9a; }
body.dark-only .gl-tab-active { color: #4caf9a; border-bottom-color: #4caf9a; }
body.dark-only .gl-tab-bar { border-bottom-color: #2a3432; }

body.dark-only .gl-form-panel { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-label { color: #8ab5ad; }
body.dark-only .gl-input, body.dark-only .gl-textarea, body.dark-only .gl-select {
  background: #16211f; border-color: #2a3d3a; color: #e0eded;
}
body.dark-only .gl-input:focus, body.dark-only .gl-textarea:focus, body.dark-only .gl-select:focus {
  border-color: #4caf9a; background: #1e2d2b;
}
body.dark-only .gl-audience-card { background: #16211f; border-color: #2a3d3a; }
body.dark-only .gl-audience-card:hover, body.dark-only .gl-audience-active { background: #1c2f2b !important; border-color: #4caf9a !important; }
body.dark-only .gl-aud-label { color: #e0eded; }
body.dark-only .gl-form-actions { border-top-color: #2a3d3a; }
body.dark-only .gl-btn-ghost { background: #1e2d2b; border-color: #2a3d3a; color: #8ab5ad; }
body.dark-only .gl-btn-ghost:hover { background: #243432; }
body.dark-only .gl-btn-preview { background: #1e2d2b; border-color: #4caf9a; color: #4caf9a; }
body.dark-only .gl-recipient-badge { background: #1c3530; color: #4caf9a; }
body.dark-only .gl-preview-meta-chip { background: #1c3530; color: #4caf9a; }

body.dark-only .gl-history-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-history-title { color: #e0eded; }
body.dark-only .gl-history-msg { color: #7a9e9a; }
body.dark-only .gl-skel-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
body.dark-only .gl-empty { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-empty-title { color: #e0eded; }
body.dark-only .gl-modal { background: #1e2d2b; }
body.dark-only .gl-modal-title { color: #e0eded; }
body.dark-only .gl-modal-sub { color: #7a9e9a; }
body.dark-only .gl-modal-actions .gl-btn-ghost { background: #16211f; border-color: #2a3d3a; }
</style>
